import React, { useEffect, useState, useMemo, useRef } from "react";
import { PageContainer } from "../components/Container/PageContainer";
import { Box, Button } from "@mui/material";
import { KeyMetricSummarySection } from "../template/metric/KeyMetricSummarySection";
import moment from "moment";
import { WeeklyPostSectionCustomGrid } from "../template/post/WeeklyPostSectionCustomGrid";
import ViewportWrapper from "../components/triggerWrapper/ViewportWrapper";
import { trackEvent } from "../utils/analytics";
import { DataLoadError } from "../components/error/DataLoadError";
import { useTranslation } from "react-i18next";
import { useDashboardTimeline } from "../contexts/ThreadsContext";
import { format } from "date-fns";
import { useIsMobile } from "../hooks/useIsMobile";

const Dashboard = ({ profile, userLoading, error }) => {
  const { t } = useTranslation();

  // 로컬 상태 (UI 관리용) - 모든 hooks를 상단에 배치
  const [dataError, setDataError] = useState(null);
  const [isDataLoadFailed, setIsDataLoadFailed] = useState(false);

  // 반응형 Hook
  const isSM = useIsMobile("sm");

  // 현재 표시 중인 월/년 상태
  const today = new Date();
  const [displayMonth, setDisplayMonth] = useState(format(today, "MM"));
  const [displayYear, setDisplayYear] = useState(format(today, "yyyy"));

  // Refs (스크롤 관련 ref 제거)
  const lastElementRef = useRef(null);

  // Timeline hooks 사용 - 무조건 호출해야 함 (Hook 규칙)
  const timelineHookResult = useDashboardTimeline();

  // Fallback values for when hook fails or returns incomplete data
  const safeTimelineData =
    timelineHookResult && typeof timelineHookResult === "object"
      ? timelineHookResult
      : {
          timeline: {
            months: [],
            isLoadingMore: false,
            hasMoreFromAPI: false,
            hasMoreFromLifetime: false,
          },
          isInitialPhase: true,
          hasTimelineData: false,
          canUseLifetimeCache: false,
          loadMore: async () => false,
          initializeTimeline: async () => {},
          handleLifetimeComplete: () => {},
        };

  const {
    timeline,
    isInitialPhase,
    isAPILoadingPhase,
    hasTimelineData,
    hasLifetimeData,
    canUseLifetimeCache,
    loadMore,
    initializeTimeline,
    handleLifetimeComplete,
  } = safeTimelineData;

  // 현재 표시할 월의 인사이트 데이터 계산
  const currentMonthInsight = useMemo(() => {
    if (!hasTimelineData || !timeline?.months || timeline.months.length === 0)
      return null;

    const targetMonth = parseInt(displayMonth);
    const targetYear = parseInt(displayYear);

    return (
      timeline.months.find(
        (item) => item && item.month === targetMonth && item.year === targetYear
      )?.insight || null
    );
  }, [timeline?.months, displayMonth, displayYear, hasTimelineData]);

  // 월별 스레드 데이터 변환 (기존 컴포넌트와 호환)
  const threadsList = useMemo(() => {
    if (!hasTimelineData || !timeline.months || timeline.months.length === 0)
      return [];

    return timeline.months
      .filter(
        (monthData) =>
          monthData &&
          monthData.month !== undefined &&
          monthData.year !== undefined
      )
      .map((monthData) => ({
        month: monthData.month.toString().padStart(2, "0"),
        year: monthData.year.toString(),
        week: monthData.threads?.week || [],
      }))
      .sort((a, b) => {
        // 최신 월부터 정렬
        if (a.year !== b.year) return parseInt(b.year) - parseInt(a.year);
        return parseInt(b.month) - parseInt(a.month);
      });
  }, [timeline?.months, hasTimelineData]);

  // 로딩 상태 계산 - 개선된 로직
  const isLoadingMore = timeline.isLoadingMore;
  const isInitialLoading = isInitialPhase && threadsList.length === 0;
  
  const hasMoreData =
    timeline.hasMoreFromAPI ||
    (canUseLifetimeCache && timeline.hasMoreFromLifetime);

  // 메트릭 섹션 로딩 상태
  const isMetricLoading = currentMonthInsight === null && (isInitialLoading || isLoadingMore);

  // 데이터 재시도 함수
  const retryDataLoad = async () => {
    setDataError(null);
    setIsDataLoadFailed(false);

    try {
      await initializeTimeline();
    } catch (error) {
      console.error("Dashboard data retry failed:", error);
      setDataError(error.message || t("error.dataLoadFailed"));
      setIsDataLoadFailed(true);
    }
  };

  // 이전 월 로드 함수 (Timeline에 새로운 month push)
  const onLoadPreviousMonth = async () => {
    try {
      console.log(`📅 Dashboard: 이전 월 데이터 로드 시작`);
      console.log(`📅 Timeline 상태:`, {
        phase: timeline.phase,
        hasMoreFromAPI: timeline.hasMoreFromAPI,
        hasMoreFromLifetime: timeline.hasMoreFromLifetime,
        isLoadingMore: timeline.isLoadingMore,
        canUseLifetimeCache,
        monthsCount: timeline.months?.length || 0
      });
      
      const success = await loadMore();
      if (success) {
        console.log(`✅ Dashboard: 이전 월 로드 요청 성공`);
      } else {
        console.log(`ℹ️ Dashboard: 더 이상 로드할 데이터가 없음`);
        console.log(`ℹ️ 현재 Timeline 상태:`, {
          hasMoreFromAPI: timeline.hasMoreFromAPI,
          hasMoreFromLifetime: timeline.hasMoreFromLifetime,
          canUseLifetimeCache
        });
      }
    } catch (error) {
      console.error(`❌ Dashboard: 이전 월 로드 실패:`, error);
      setDataError(error.message || t("error.dataLoadFailed"));
    }
  };

  // Timeline 상태 변화 모니터링 (디버깅용)
  useEffect(() => {
    console.log(`📊 Dashboard: Timeline 상태 업데이트됨`, {
      monthsCount: timeline.months?.length || 0,
      threadsListLength: threadsList.length,
      hasTimelineData,
      phase: timeline.phase,
      isLoadingMore: timeline.isLoadingMore
    });
  }, [timeline.months, threadsList.length, hasTimelineData, timeline.phase, timeline.isLoadingMore]);

  // 새로운 월 추가 감지
  useEffect(() => {
    if (timeline.months && timeline.months.length > 0) {
      const monthKeys = timeline.months
        .filter(m => m && m.year && m.month)
        .map(m => `${m.year}-${m.month.toString().padStart(2, '0')}`);
      console.log(`📅 Dashboard: 현재 로드된 월들:`, monthKeys);
    }
  }, [timeline.months?.length]);

  // 초기 타임라인 로드 (오늘 날짜 월부터 시작)
  useEffect(() => {
    // Storybook mock 데이터 처리
    if (window.mockMonthlyThreadsData && window.mockMonthlyInsightData) {
      // Timeline에 mock 데이터 추가하는 로직 필요
      // 현재는 mock 환경에서 스킵
      return;
    }

    if (isInitialPhase && !userLoading && !error && timelineHookResult) {
      console.log(`🚀 Dashboard: 오늘 날짜(${displayYear}-${displayMonth}) 월별 데이터 초기 로드 시작`);
      
      initializeTimeline().catch((error) => {
        console.error("Timeline initialization failed:", error);
        setDataError(error.message || t("error.dataLoadFailed"));
        setIsDataLoadFailed(true);
      });
    }
  }, [
    isInitialPhase,
    userLoading,
    error,
    initializeTimeline,
    timelineHookResult,
    displayYear,
    displayMonth,
    t,
  ]);

  // Lifetime 완료 감지
  useEffect(() => {
    if (hasLifetimeData && isAPILoadingPhase && timelineHookResult) {
      handleLifetimeComplete();
    }
  }, [
    hasLifetimeData,
    isAPILoadingPhase,
    handleLifetimeComplete,
    timelineHookResult,
  ]);

  // 스크롤 조정 제거 - Main에서 스크롤 제어

  // Hook error 처리
  if (!timelineHookResult || typeof timelineHookResult !== "object") {
    return (
      <PageContainer>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: 3,
            py: 3,
          }}
        >
          <DataLoadError
            title="Timeline Hook Error"
            message="Failed to initialize timeline hooks. Please refresh the page."
            showRetry={true}
            onRetry={() => window.location.reload()}
          />
        </Box>
      </PageContainer>
    );
  }

  // 사용자 인증 상태 처리
  if (userLoading) {
    return (
      <PageContainer>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: 3,
            py: 3,
          }}
        >
          <div>사용자 정보를 불러오는 중...</div>
        </Box>
      </PageContainer>
    );
  }
  
  if (error) {
    return (
      <PageContainer>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: 3,
            py: 3,
          }}
        >
          <DataLoadError
            title="인증 오류"
            message={error}
            showRetry={false}
          />
        </Box>
      </PageContainer>
    );
  }

  // 초기 로딩 상태 - 스켈레톤 UI로 일관성 유지
  if (isInitialLoading && !isDataLoadFailed) {
    return (
      <PageContainer>
        <KeyMetricSummarySection
          loading={true}
          year={displayYear || ""}
          month={displayMonth || ""}
          profileViews={0}
          views={0}
          likes={0}
          replies={0}
          reposts={0}
          postLoading={true}
          monthlyViews={[]}
          dataError={null}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: 3,
            py: 3,
          }}
        >
          <div>{displayYear}년 {displayMonth}월 데이터를 불러오는 중...</div>
        </Box>
      </PageContainer>
    );
  }

  if (isDataLoadFailed && threadsList.length === 0) {
    return (
      <PageContainer>
        {/* <KeyMetricSummarySection
          loading={false}
          year={displayYear || ""}
          month={displayMonth || ""}
          profileViews={0}
          views={0}
          likes={0}
          replies={0}
          reposts={0}
          postLoading={false}
          monthlyViews={[]}
          dataError={dataError}
        /> */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: 3,
            py: 3,
          }}
        >
          <DataLoadError
            title={t("error.dataLoadFailed")}
            message={t("error.threadsDataLoadFailed")}
            showRetry={true}
            onRetry={retryDataLoad}
          />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <KeyMetricSummarySection
        loading={isMetricLoading}
        year={displayYear || ""}
        month={displayMonth || ""}
        profileViews={
          currentMonthInsight?.profileViews?.reduce(
            (sum, item) => sum + item.y,
            0
          ) || 0
        }
        views={currentMonthInsight?.views || 0}
        likes={currentMonthInsight?.likes || 0}
        replies={currentMonthInsight?.replies || 0}
        reposts={currentMonthInsight?.reposts || 0}
        postLoading={isInitialLoading || isLoadingMore}
        monthlyViews={currentMonthInsight?.profileViews || []}
        dataError={dataError}
      />
      <Box
        id={"container"}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          pt: isSM ? '224px' : '264px',
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, pb: 0, width: '100%' }}>
          {threadsList.map((monthlyData, i) => {
            return (
              <Box
                sx={{ display: 'flex', flexDirection: 'column' }}
                ref={i === threadsList.length - 1 ? lastElementRef : null}
                key={i}
              >
                <ViewportWrapper
                  onEnterViewport={() => {
                    const newMonth = threadsList[i].month;
                    const newYear = threadsList[i].year;
                    console.log(`📍 Dashboard: 화면에 표시되는 월 변경: ${newYear}-${newMonth}`);
                    setDisplayMonth(newMonth);
                    setDisplayYear(newYear);
                  }}
                  onLeaveViewport={() => {
                    // 스크롤 방향 로직 제거 - Main에서 스크롤 제어
                  }}
                >
                  <WeeklyPostSectionCustomGrid
                    key={i}
                    threadsList={monthlyData}
                    profile={profile}
                    setDisplayMonth={setDisplayMonth}
                    setDisplayYear={setDisplayYear}
                  />
                </ViewportWrapper>
              </Box>
            );
          })}
          {hasMoreData && (
            <Button
              size="large"
              variant="outlined"
              color="primary"
              onClick={() => {
                console.log(`🔄 Dashboard: 이전 달 불러오기 버튼 클릭`);
                onLoadPreviousMonth();
                trackEvent("load_previous_month");
              }}
              disabled={isLoadingMore}
              sx={{
                mt: 3,
                mb: 10,
              }}
            >
             {isLoadingMore ? 'Loading...' : t("dashboard.loadPreviousMonth")}
            </Button>
          )}
        </Box>
      </Box>
    </PageContainer>
  );
};

export default Dashboard;
